import React, { useState } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, ChevronRight, ChevronLeft, Check, X } from 'lucide-react';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Avatar, Help } from './shared';

const SOURCES = [
  { v: 'phone', label: '📞 Telefonat' },
  { v: 'in_person', label: '🤝 Persönlich' },
  { v: 'video', label: '💻 Video-Call' },
];

const STEPS = [
  { k: 'situation', label: 'Situation', color: 'text-sky-400',
    q: 'Über welche Situation habt ihr gesprochen?',
    ph: 'Im Verkäufergespräch mit [Firma] am Dienstag …',
    help: 'Je konkreter der Moment, desto mehr bleibt vom Gespräch erhalten.' },
  { k: 'behavior', label: 'Verhalten', color: 'text-violet-400',
    q: 'Welches Verhalten wurde beschrieben?',
    ph: 'Du hast dreimal unterbrochen, bevor der Verkäufer fertig war …',
    help: 'Halte fest, was beobachtet wurde — nicht die Interpretation.' },
  { k: 'impact', label: 'Wirkung', color: 'text-amber-400',
    q: 'Welche Wirkung wurde geschildert?',
    ph: 'Auf ihn/sie wirkte es, als ob … / Es kam an wie …',
    help: 'Die Wahrnehmung deines Gesprächspartners — so wie er/sie sie beschrieben hat.' },
  { k: 'feedforward', label: 'Vorschlag (optional)', color: 'text-emerald-400',
    q: 'Gab es einen Vorschlag für nächstes Mal?',
    ph: 'Probier mal, die ersten 5 Minuten nur Fragen zu stellen …',
    help: 'Falls im Gespräch eine Idee fiel — hier ist ihr Platz.' },
];

export default function ConversationDialog({ members, meId, myGoals, onClose, onSaved }) {
  const [step, setStep] = useState(0);
  const [partnerId, setPartnerId] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [source, setSource] = useState('phone');
  const [form, setForm] = useState({ situation: '', behavior: '', impact: '', feedforward: '' });
  const [goalId, setGoalId] = useState('');
  const [saving, setSaving] = useState(false);
  const others = members.filter((m) => m.id !== meId);
  const displayName = partnerId ? (others.find((m) => m.id === partnerId)?.name || '') : partnerName;

  const next = () => {
    if (step === 0 && !partnerId && !partnerName.trim()) return toast('Mit wem hast du gesprochen? Name genügt.');
    setStep((s) => s + 1);
  };

  const save = async () => {
    if (!form.situation.trim() || !form.behavior.trim() || !form.impact.trim())
      return toast('Situation, Verhalten und Wirkung — so bleibt das Gespräch greifbar.');
    setSaving(true);
    try {
      await api.teamDevConversation({
        from_user_id: partnerId, from_name: partnerName.trim(), source,
        ...form, related_goal_id: goalId,
      });
      toast.success(`Festgehalten — das Gespräch mit ${displayName} ist jetzt in deinem Postfach. 🌱`, { duration: 5000 });
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern hat nicht geklappt');
    } finally { setSaving(false); }
  };

  const totalSteps = STEPS.length + 2;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" data-testid="td-conversation-dialog">
      <div className="bg-card border border-border rounded-lg p-7 max-w-lg w-full td-pop max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-medium text-foreground">🗣 Gespräch festhalten</p>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="td-conversation-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center gap-1 mb-5">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <span key={i} className={`h-1 rounded-full transition-all ${i === step ? 'w-6 bg-emerald-400' : i < step ? 'w-3 bg-emerald-400/40' : 'w-3 bg-border'}`} />
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-4 td-pop" data-testid="td-conversation-step-partner">
            <p className="text-base text-foreground">Mit wem hast du gesprochen?</p>
            <div className="flex flex-wrap gap-2">
              {others.map((m) => (
                <button key={m.id} onClick={() => { setPartnerId(m.id); setPartnerName(''); }} data-testid={`td-conv-partner-${m.id}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    partnerId === m.id ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-border bg-secondary/30 text-foreground hover:bg-secondary/60'}`}>
                  <Avatar name={m.name} className="w-4 h-4 text-[9px]" />
                  {m.name}
                </button>
              ))}
            </div>
            <div>
              <Input value={partnerName} onChange={(e) => { setPartnerName(e.target.value); setPartnerId(''); }}
                placeholder="… oder Name eintippen (falls noch kein Account)" className="text-sm bg-background rounded-md"
                data-testid="td-conv-partner-name" />
              <Help>Z. B. für Gespräche mit Rüdiger oder Henning, solange sie noch nicht freigeschaltet sind.</Help>
            </div>
            <div>
              <p className="text-xs font-medium text-foreground mb-1.5">Wie habt ihr gesprochen?</p>
              <div className="flex gap-2">
                {SOURCES.map((s) => (
                  <button key={s.v} onClick={() => setSource(s.v)} data-testid={`td-conv-source-${s.v}`}
                    className={`text-xs rounded-full border px-3 py-1.5 transition-colors ${
                      source === s.v ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step >= 1 && step <= STEPS.length && (() => {
          const s = STEPS[step - 1];
          return (
            <div className="space-y-3 td-pop" key={s.k} data-testid={`td-conversation-step-${s.k}`}>
              <p className={`text-[10px] font-mono uppercase tracking-wider ${s.color}`}>{s.label}</p>
              <p className="text-base text-foreground leading-snug">{s.q}</p>
              <Textarea value={form[s.k]} onChange={(e) => setForm((f) => ({ ...f, [s.k]: e.target.value }))}
                rows={4} placeholder={s.ph} autoFocus
                className="text-sm bg-background rounded-md" data-testid={`td-conv-field-${s.k}`} />
              <Help>{s.help}</Help>
            </div>
          );
        })()}

        {step === totalSteps - 1 && (
          <div className="space-y-3 td-pop" data-testid="td-conversation-step-review">
            <p className="text-base text-foreground">Kurz prüfen — dann ab ins Postfach.</p>
            <div className="bg-background/60 border border-border rounded-md p-3.5 space-y-1.5 text-[12px]">
              <p><span className="text-muted-foreground">Gespräch mit</span> <span className="font-medium text-foreground">{displayName}</span> · {SOURCES.find((s) => s.v === source)?.label}</p>
              {STEPS.map((s) => form[s.k] && (
                <p key={s.k}><span className={`font-mono text-[9px] uppercase mr-1 ${s.color}`}>{s.label}:</span><span className="text-foreground/90">{form[s.k]}</span></p>
              ))}
            </div>
            {myGoals.length > 0 && (
              <div>
                <p className="text-xs font-medium text-foreground mb-1.5">Passt es zu einem deiner Ziele?</p>
                <div className="flex flex-wrap gap-1.5">
                  {myGoals.map((g) => (
                    <button key={g.id} onClick={() => setGoalId((v) => (v === g.id ? '' : g.id))} data-testid={`td-conv-goal-${g.id}`}
                      className={`text-[10px] rounded-full border px-2.5 py-1 transition-colors ${goalId === g.id ? 'border-sky-500/50 bg-sky-500/15 text-sky-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                      {goalId === g.id ? '✓ ' : ''}{g.title}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <p className="text-[10px] text-muted-foreground">Diese Notiz sieht nur du — dein Gesprächspartner bekommt nichts davon mit.</p>
          </div>
        )}

        <div className="flex gap-2 mt-6">
          {step > 0 && (
            <Button variant="outline" onClick={() => setStep((s) => s - 1)} className="rounded-full" data-testid="td-conversation-back">
              <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
            </Button>
          )}
          {step < totalSteps - 1 ? (
            <Button onClick={next} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full flex-1" data-testid="td-conversation-next">
              Weiter <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full flex-1" data-testid="td-conversation-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />} Festhalten
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
